import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProjectProvider } from "@/context/ProjectContext";
import { NotificationProvider } from "@/context/NotificationContext";
import ErrorBoundary from "@/components/ErrorBoundary";

import ResponsiveLayout from "./components/mobile/ResponsiveLayout";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import EditProject from "./pages/EditProject";
import NewProject from "./pages/NewProject";
import NewSubmission from "./pages/NewSubmission";
import Users from "./pages/Users";
import Settings from "./pages/Settings";
import NotFound from "./pages/NotFound";
import Notifications from "./pages/Notifications";
import ProjectInviteAccept from "./pages/ProjectInviteAccept";
import AcceptInvitations from "./pages/AcceptInvitations";
import EditSubmission from "./pages/EditSubmission";
import PWADebug from "./pages/PWADebug";
import CompanyResourceLibrary from "./pages/CompanyResourceLibrary";
import ReportBug from "./pages/ReportBug";
import AHJDatabase from "./pages/AHJDatabase";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useAuthRedirect } from "./hooks/useAuthRedirect";
import PartBuilder from "./pages/studio/PartBuilder";
import ConnectionBuilder from "./pages/studio/ConnectionBuilder";

// Protected route component - moved inside the Routes component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { getLoginUrlWithRedirect } = useAuthRedirect();

  // While checking authentication status, show nothing
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  // If not authenticated, redirect to login with current URL
  if (!isAuthenticated) {
    return <Navigate to={getLoginUrlWithRedirect()} replace />;
  }

  // If authenticated, render children
  return <>{children}</>;
};

// Create a new QueryClient instance inside the component
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  },
});

if (typeof window !== "undefined") {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  window.__TANSTACK_QUERY_CLIENT__ = queryClient;
}

const App = () => {
  const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        <BrowserRouter>
          <AuthProvider>
            <NotificationProvider>
              <ProjectProvider>
                <TooltipProvider>
                  <Toaster />
                  <Sonner />
                  <SentryRoutes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route
                      path="/forgot-password"
                      element={<ForgotPassword />}
                    />
                    <Route path="/reset-password" element={<ResetPassword />} />

                    {/* Studio Routes */}
                    <Route
                      path="/studio/part-builder"
                      element={
                        <ProtectedRoute>
                          <ResponsiveLayout>
                            <PartBuilder />
                          </ResponsiveLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/studio/connection-builder"
                      element={
                        <ProtectedRoute>
                          <ResponsiveLayout>
                            <ConnectionBuilder />
                          </ResponsiveLayout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <ResponsiveLayout>
                            <Dashboard />
                          </ResponsiveLayout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/projects"
                      element={
                        <ProtectedRoute>
                          <ResponsiveLayout>
                            <Projects />
                          </ResponsiveLayout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/projects/:id"
                      element={
                        <ProtectedRoute>
                          <ResponsiveLayout>
                            <ErrorBoundary>
                              <ProjectDetail />
                            </ErrorBoundary>
                          </ResponsiveLayout>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/projects/:id/edit"
                      element={
                        <ProtectedRoute>
                          <ResponsiveLayout>
                            <EditProject />
                          </ResponsiveLayout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/projects/new"
                      element={
                        <ProtectedRoute>
                          <ResponsiveLayout>
                            <NewProject />
                          </ResponsiveLayout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/projects/:projectId/invite/accept"
                      element={<ProjectInviteAccept />}
                    />

                    <Route
                      path="/accept-invitations"
                      element={
                        <ProtectedRoute>
                          <AcceptInvitations />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/projects/:projectId/submissions/new"
                      element={
                        <ProtectedRoute>
                          <ResponsiveLayout>
                            <NewSubmission />
                          </ResponsiveLayout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/projects/:projectId/submissions/edit"
                      element={
                        <ProtectedRoute>
                          <ResponsiveLayout>
                            <EditSubmission />
                          </ResponsiveLayout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/users"
                      element={
                        <ProtectedRoute>
                          <ResponsiveLayout>
                            <Users />
                          </ResponsiveLayout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute>
                          <ResponsiveLayout>
                            <Settings />
                          </ResponsiveLayout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/resources"
                      element={
                        <ProtectedRoute>
                          <ResponsiveLayout>
                            <CompanyResourceLibrary />
                          </ResponsiveLayout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/ahj-database"
                      element={
                        <ProtectedRoute>
                          <ResponsiveLayout>
                            <AHJDatabase />
                          </ResponsiveLayout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/report-bug"
                      element={
                        <ProtectedRoute>
                          <ResponsiveLayout>
                            <ReportBug />
                          </ResponsiveLayout>
                        </ProtectedRoute>
                      }
                    />

                    {/* PWA Debug route */}
                    <Route
                      path="/pwa-debug"
                      element={
                        <ProtectedRoute>
                          <ResponsiveLayout>
                            <PWADebug />
                          </ResponsiveLayout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Notifications route */}
                    <Route
                      path="/notifications"
                      element={
                        <ProtectedRoute>
                          <ResponsiveLayout>
                            <Notifications />
                          </ResponsiveLayout>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/new-submission"
                      element={
                        <ProtectedRoute>
                          <ResponsiveLayout>
                            <NewSubmission />
                          </ResponsiveLayout>
                        </ProtectedRoute>
                      }
                    />

                    {/* Project routes with submissions */}
                    <Route
                      path="/projects/:id/submissions/:submissionId"
                      element={
                        <Navigate to="/submissions/:submissionId" replace />
                      }
                    />

                    {/* 404 route */}
                    <Route path="*" element={<NotFound />} />
                  </SentryRoutes>
                </TooltipProvider>
              </ProjectProvider>
            </NotificationProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
